import { useCallback, useMemo } from 'react';
import useHttp from './useHttp';
import { useAuth } from '../context/authContext';

const useWebAuthnApi = () => {
  const { sendRequest } = useHttp();
  const authCtx = useAuth();

  const webauthnLoginStart = useCallback(async () => {
    return sendRequest({
      url: '/api/v1/auth/webauthn/login/start',
      method: 'POST',
      headers: authCtx.getAuthHeaders({ withAuth: false, withDevice: true }),
      body: {},
    });
  }, [authCtx, sendRequest]);

  const webauthnLoginFinish = useCallback(
    async ({ challenge, credential, isPrivateDevice }) => {
      return sendRequest({
        url: '/api/v1/auth/webauthn/login/finish',
        method: 'POST',
        headers: authCtx.getAuthHeaders({ withAuth: false, withDevice: true }),
        body: { challenge, credential, isPrivateDevice },
      });
    },
    [authCtx, sendRequest]
  );

  const webauthnRegisterStartAuth = useCallback(async () => {
    return sendRequest({
      url: '/api/v1/auth/webauthn/register/start-auth',
      method: 'POST',
      headers: authCtx.getAuthHeaders({ withAuth: true, withDevice: true }),
      body: {},
    });
  }, [authCtx, sendRequest]);

  const webauthnRegisterFinishAuth = useCallback(
    async ({ challenge, credential, credentialName }) => {
      return sendRequest({
        url: '/api/v1/auth/webauthn/register/finish-auth',
        method: 'POST',
        headers: authCtx.getAuthHeaders({ withAuth: true, withDevice: true }),
        body: { challenge, credential, credentialName },
      });
    },
    [authCtx, sendRequest]
  );

  const getWebauthnStatus = useCallback(async () => {
    return sendRequest({
      url: '/api/v1/auth/webauthn/status',
      method: 'GET',
      headers: authCtx.getAuthHeaders({ withAuth: true, withDevice: true }),
    });
  }, [authCtx, sendRequest]);

  const getWebauthnCredentials = useCallback(async () => {
    return sendRequest({
      url: '/api/v1/auth/webauthn/credentials',
      method: 'GET',
      headers: authCtx.getAuthHeaders({ withAuth: true, withDevice: true }),
    });
  }, [authCtx, sendRequest]);

  const deleteWebauthnCredential = useCallback(
    async credentialId => {
      return sendRequest({
        url: `/api/v1/auth/webauthn/credentials/${encodeURIComponent(credentialId)}`,
        method: 'DELETE',
        headers: authCtx.getAuthHeaders({ withAuth: true, withDevice: true }),
      });
    },
    [authCtx, sendRequest]
  );

  const webauthnStepupStart = useCallback(
    async ({ requiredRights = [] } = {}) => {
      return sendRequest({
        url: '/api/v1/auth/webauthn/stepup/start',
        method: 'POST',
        headers: authCtx.getAuthHeaders({ withAuth: true, withDevice: true }),
        body: {
          requiredRights: Array.isArray(requiredRights) ? requiredRights : [],
        },
      });
    },
    [authCtx, sendRequest]
  );

  const webauthnStepupFinish = useCallback(
    async ({ challenge, credential }) => {
      return sendRequest({
        url: '/api/v1/auth/webauthn/stepup/finish',
        method: 'POST',
        headers: authCtx.getAuthHeaders({ withAuth: true, withDevice: true }),
        body: { challenge, credential },
      });
    },
    [authCtx, sendRequest]
  );

  return useMemo(
    () => ({
      webauthnLoginStart,
      webauthnLoginFinish,
      webauthnRegisterStartAuth,
      webauthnRegisterFinishAuth,
      getWebauthnStatus,
      getWebauthnCredentials,
      deleteWebauthnCredential,
      webauthnStepupStart,
      webauthnStepupFinish,
    }),
    [
      webauthnLoginStart,
      webauthnLoginFinish,
      webauthnRegisterStartAuth,
      webauthnRegisterFinishAuth,
      getWebauthnStatus,
      getWebauthnCredentials,
      deleteWebauthnCredential,
      webauthnStepupStart,
      webauthnStepupFinish,
    ]
  );
};

export default useWebAuthnApi;
